import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clinicService from '../services/clinicService';
import userService from '../services/userService';
import { refId, repName } from '../utils/repTree';
import { useT } from '../i18n/LanguageContext';

/**
 * ACCESS - what one rep can see, as two checkbox lists.
 *
 *   ACCOUNTS  -> the practices whose records they see, and the only ones they may submit for.
 *   REPS      -> other reps whose submissions they see, wherever those records sit.
 *
 * The two lists are independent IN THE DATA: `visibleReps` grants sight of submissions and grants
 * no practices at all. Ticking a rep here also ticks that rep's accounts above, but that is a
 * SHORTCUT IN THIS DIALOG, not inheritance - it saves an admin fourteen clicks for a rep with
 * fourteen accounts, and what gets stored is the resulting explicit list. Nothing rolls up later:
 * if that rep gains an account tomorrow, it does not appear here on its own.
 *
 * BOTH LISTS SAVE TOGETHER, on one button, and only what CHANGED is sent. Two independent
 * auto-saves would mean a half-applied access change with no way to tell which half landed; one
 * button means the dialog either applied your edit or told you it did not.
 */

const unwrap = (res) => {
  const data = res?.data?.data?.data || res?.data?.data || [];
  return Array.isArray(data) ? data : [];
};

/** Same members, order-independent. Decides whether a list is worth sending at all. */
const sameSet = (a, b) => a.length === b.length && a.every((v) => b.includes(v));

/**
 * Which clinics to OFFER. The admin list is the raw QuickBooks customer roster, and that roster is
 * mostly not clinics: bank-feed rows, transfers, Stripe and Gusto, suppliers, individuals. An admin
 * has already ruled on most of them (`isPractice: false`), and the rep's own clinic picker honours
 * that ruling - so this dialog must too, or it offers a rep an account they could never submit for.
 * The default here is the picker's rule exactly: active, and not ruled out.
 *
 * ANYTHING ALREADY TICKED IS SHOWN REGARDLESS. If a clinic were ruled out after being assigned, it
 * would vanish from this list while still being ticked on the record - and the next save would send
 * a list that no longer contains it, silently revoking access nobody meant to revoke.
 */
/** The clinic ids a rep row covers, as strings. Populated refs and raw ids both work. */
const clinicIdsOf = (rep) => (rep?.clinics || []).map(refId).filter(Boolean);

const offerableClinics = (clinics, ticked, showAll) =>
  clinics.filter(
    (c) =>
      showAll ||
      ticked.includes(String(c._id)) ||
      (c.isActive !== false && c.isPractice !== false),
  );

export function CheckList({ items, checked, onToggle, labelOf, empty, columns = 2, searchLabel }) {
  const t = useT();
  const [search, setSearch] = useState('');
  const needle = search.trim().toLowerCase();
  const shown = needle
    ? items.filter((it) => labelOf(it).toLowerCase().includes(needle))
    : items;

  return (
    <div>
      <input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t('Filter...')}
        aria-label={t(searchLabel)}
        className="mb-2 h-[32px] w-full border border-[#d6dce8] rounded-[8px] px-3 text-[12px] text-[#0f172a]"
      />
      {shown.length === 0 ? (
        <p className="text-[12px] text-[#64748b] py-2">{needle ? t('Nothing matches that.') : t(empty)}</p>
      ) : (
        <div className={`grid gap-x-4 gap-y-1.5 ${columns === 2 ? 'grid-cols-2' : 'grid-cols-1'}`}>
          {shown.map((it) => {
            const id = String(it._id);
            return (
              <label key={id} className="flex items-start gap-2 text-[12px] text-[#0f172a] cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={checked.includes(id)}
                  onChange={() => onToggle(id)}
                />
                <span className="leading-snug">{labelOf(it)}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function RepAccessModal({ open, rep, onClose, onSaved }) {
  const t = useT();
  const [clinics, setClinics] = useState([]);
  const [reps, setReps] = useState([]);
  const [checkedClinics, setCheckedClinics] = useState([]);
  const [checkedReps, setCheckedReps] = useState([]);
  const [baseline, setBaseline] = useState({ clinics: [], reps: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // A load failure is NOT the same as a save failure. If the lists never arrived, the checkboxes
  // would all render unticked - a picture of "this rep can see nothing" that is simply untrue.
  // Save is disabled in that state anyway, but showing the picture at all is the problem.
  const [loadFailed, setLoadFailed] = useState(false);
  const [showAllClinics, setShowAllClinics] = useState(false);
  /**
   * Which accounts were ticked FOR the admin by ticking a rep, keyed by that rep. Ticking Kevin
   * adds his fourteen accounts in one go - the point of the feature - and unticking him has to be
   * able to take back exactly that and nothing else.
   *
   * Only accounts that were NOT already ticked at that moment are recorded, so an account the
   * admin had before, or ticked by hand first, is never in here and can never be auto-removed.
   */
  const [autoAdded, setAutoAdded] = useState({});
  /** The last automatic change, echoed under the rep list so the bulk edit is not invisible. */
  const [autoNote, setAutoNote] = useState('');

  const repId = rep ? String(rep._id) : '';

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setLoadFailed(false);
    try {
      const [clinicRes, repRes] = await Promise.all([
        clinicService.adminList(),
        userService.getRepAccess(),
      ]);
      const allClinics = unwrap(clinicRes);
      const allReps = unwrap(repRes);
      // Read the CURRENT state from the freshly fetched row, not from the card that opened the
      // dialog - the card's copy can be minutes old, and saving from it would silently revert
      // whatever changed in between.
      const mine = allReps.find((r) => String(r._id) === repId);
      const startClinics = (mine?.clinics || []).map(refId).filter(Boolean);
      const startReps = (mine?.visibleReps || []).map(refId).filter(Boolean);

      setClinics(allClinics);
      setShowAllClinics(false);
      setReps(allReps.filter((r) => String(r._id) !== repId)); // never offer someone themselves
      setCheckedClinics(startClinics);
      setCheckedReps(startReps);
      setBaseline({ clinics: startClinics, reps: startReps });
      setAutoAdded({});
      setAutoNote('');
    } catch (err) {
      // English in state, translated at render ({t(error)}): `t` stays out of this callback so a
      // language switch cannot re-run the load and discard unsaved ticks.
      setError('Could not load access settings');
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [repId]);

  useEffect(() => {
    if (!open || !repId) return;
    load();
  }, [open, repId, load]);

  useEffect(() => {
    if (!open) return undefined;
    const onEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [open, onClose]);

  /** Plain toggle, used by the account list. A hand-ticked account is never auto-managed. */
  const toggleClinic = (id) =>
    setCheckedClinics((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  /**
   * Ticking a rep also ticks that rep's accounts; unticking takes back exactly what it added.
   *
   * The removal is deliberately narrow. An account comes off only when ALL of these hold:
   *   - this rep's tick is what added it (so it was not already ticked, and not in the baseline);
   *   - no OTHER rep still ticked also covers it - otherwise unticking Kevin would strip an
   *     account that Curtis, still ticked, brought in too.
   * Anything the admin ticked themselves is untouched in every case. The safe direction here is
   * leaving an account ticked: an extra account is visible and correctable, a silently removed one
   * is neither.
   */
  const toggleRep = (id) => {
    const target = reps.find((r) => String(r._id) === id);
    const theirs = clinicIdsOf(target);
    const name = repName(target);

    if (checkedReps.includes(id)) {
      const stillTicked = checkedReps.filter((r) => r !== id);
      const coveredByOthers = new Set(
        stillTicked.flatMap((otherId) =>
          clinicIdsOf(reps.find((r) => String(r._id) === otherId)),
        ),
      );
      const toRemove = (autoAdded[id] || []).filter((c) => !coveredByOthers.has(c));

      setCheckedReps(stillTicked);
      setCheckedClinics((prev) => prev.filter((c) => !toRemove.includes(c)));
      setAutoAdded((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setAutoNote(
        toRemove.length
          ? t(
              toRemove.length === 1
                ? 'Removed {n} account that came with {name}.'
                : 'Removed {n} accounts that came with {name}.',
              { n: toRemove.length, name },
            )
          : '',
      );
      return;
    }

    const added = theirs.filter((c) => !checkedClinics.includes(c));
    setCheckedReps((prev) => [...prev, id]);
    setCheckedClinics((prev) => [...prev, ...added]);
    setAutoAdded((prev) => ({ ...prev, [id]: added }));
    setAutoNote(
      added.length
        ? t(
            added.length === 1
              ? "Added {n} of {name}'s account above. Untick any you do not want."
              : "Added {n} of {name}'s accounts above. Untick any you do not want.",
            { n: added.length, name },
          )
        : theirs.length
          ? t("{name}'s accounts were already selected.", { name })
          : t('{name} has no accounts yet.', { name }),
    );
  };

  const dirty =
    !sameSet(checkedClinics, baseline.clinics) || !sameSet(checkedReps, baseline.reps);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      // Only send what moved. An untouched list is not re-sent, so opening the dialog and closing
      // it cannot rewrite a field somebody else just changed.
      if (!sameSet(checkedClinics, baseline.clinics)) {
        await clinicService.assignRepClinics(repId, checkedClinics);
      }
      if (!sameSet(checkedReps, baseline.reps)) {
        await userService.setVisibleReps(repId, checkedReps);
      }
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      setError(t('Could not save those changes'));
    } finally {
      setSaving(false);
    }
  };

  const shownClinics = useMemo(
    () => offerableClinics(clinics, checkedClinics, showAllClinics),
    [clinics, checkedClinics, showAllClinics],
  );
  const hiddenCount = clinics.length - shownClinics.length;

  const clinicLabel = useMemo(() => (c) => c.name || t('Unnamed clinic'), [t]);
  const repLabel = useMemo(
    () => (r) => {
      const count = (r.clinics || []).length;
      if (!count) return repName(r);
      return t(count === 1 ? '{name} ({n} account)' : '{name} ({n} accounts)', { name: repName(r), n: count });
    },
    [t],
  );

  if (!open || !rep) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label={t('Access for {name}', { name: repName(rep) })}
    >
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      <div className="relative bg-white rounded-[14px] shadow-xl w-[760px] max-w-[calc(100vw-2rem)] z-10 max-h-[88vh] flex flex-col">
        <div className="bg-[rgba(226,232,240,0.2)] border-b border-[#e2e8f0] rounded-t-[14px] px-5 py-4 flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-[16px] font-semibold text-[#0f172a]">{t('Access — {name}', { name: repName(rep) })}</h2>
            <p className="text-[12px] text-[#64748b] mt-0.5">
              {t("Tick the accounts and the reps this person can see. Ticking a rep also ticks that rep's accounts, which you can then adjust — what is saved is exactly what is ticked.")}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('Close')}
            className="w-6 h-6 flex items-center justify-center text-[#64748b] hover:text-[#0f172a] shrink-0"
          >
            <span className="material-symbols-outlined text-[24px]">cancel</span>
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-[#0089ff]" />
          </div>
        ) : loadFailed ? (
          <div className="px-5 py-8 text-center">
            <p className="text-[13px] text-[#0f172a] font-medium">{t(error)}</p>
            <p className="text-[12px] text-[#64748b] mt-1">
              {t('Nothing has been changed. Close and reopen to try again.')}
            </p>
          </div>
        ) : (
          <div className="px-5 py-4 overflow-y-auto">
            <section className="mb-5">
              <div className="flex items-baseline justify-between mb-2">
                <h3 className="text-[13px] font-semibold text-[#0f172a]">{t('Accounts they can see')}</h3>
                <span className="text-[11px] text-[#64748b]">
                  {t('{n} of {total} selected · also what they may submit for', {
                    n: checkedClinics.length,
                    total: shownClinics.length,
                  })}
                </span>
              </div>
              {(hiddenCount > 0 || showAllClinics) && (
                <p className="text-[11px] text-[#64748b] mb-2">
                  {showAllClinics
                    ? t('Showing every QuickBooks customer, including rows ruled not a practice.')
                    : t(
                        hiddenCount === 1
                          ? '{n} QuickBooks customer is hidden — bank rows, suppliers and anything ruled not a practice.'
                          : '{n} QuickBooks customers are hidden — bank rows, suppliers and anything ruled not a practice.',
                        { n: hiddenCount },
                      )}{' '}
                  <button
                    type="button"
                    onClick={() => setShowAllClinics((v) => !v)}
                    className="underline text-[#0089ff]"
                  >
                    {showAllClinics ? t('Show practices only') : t('Show all')}
                  </button>
                </p>
              )}
              <CheckList
                items={shownClinics}
                checked={checkedClinics}
                onToggle={toggleClinic}
                labelOf={clinicLabel}
                empty="No clinics yet — run Sync from QuickBooks first."
                searchLabel="Filter accounts"
              />
            </section>

            <section>
              <div className="flex items-baseline justify-between mb-2">
                <h3 className="text-[13px] font-semibold text-[#0f172a]">{t('Reps they can see')}</h3>
                <span className="text-[11px] text-[#64748b]">
                  {t('{n} of {total} selected · ticking one also ticks their accounts above', {
                    n: checkedReps.length,
                    total: reps.length,
                  })}
                </span>
              </div>
              <CheckList
                items={reps}
                checked={checkedReps}
                onToggle={toggleRep}
                labelOf={repLabel}
                empty="No other sales reps yet."
                searchLabel="Filter reps"
              />
              {!!autoNote && (
                <p className="text-[11px] text-[#0089ff] mt-2" role="status">
                  {autoNote}
                </p>
              )}
            </section>
          </div>
        )}

        {!!error && !loadFailed && (
          <div className="px-5 shrink-0">
            <p className="text-[12px] text-red-600 pb-2">{t(error)}</p>
          </div>
        )}

        <div className="border-t border-[#e2e8f0] px-5 py-3 flex items-center justify-end gap-2 shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="h-[38px] px-4 rounded-[10px] border border-[#e2e8f0] text-xs font-semibold text-[#0f172a] hover:bg-gray-50 disabled:opacity-50"
          >
            {t('Cancel')}
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || loading || loadFailed || !dirty}
            className="h-[38px] px-5 rounded-[10px] bg-[#0089ff] text-white text-xs font-semibold hover:bg-[#0077e6] disabled:opacity-50"
          >
            {saving ? t('Saving...') : t('Save access')}
          </button>
        </div>
      </div>
    </div>
  );
}
